import { Box, Stack, Typography } from '@mui/material';
import { FunctionComponent, useRef } from 'react';
import { gamePlayStore, useGameplaySelector } from '../../store/GameplayStore';
import { useAppDispatch } from '../../store/Hooks';
import { devSwitch } from '../../util/Environment';
import { muiStyleSheet } from '../../util/Styles';
import { LOREM_IPSUM } from '../../util/Text';
import { FlipCard } from '../animation/FlipCard';
import { TwoColumns } from '../layout/Columns';
import { useLineCount } from '../layout/LayoutUtils';
import { AnswerCard } from './AnswerCard';

// Useful for testing layout
const FORCE_ANSWER_COUNT = devSwitch(false) ? 5 : undefined;

export const QuestionWithAnswers: FunctionComponent = () => {
	const question = useGameplaySelector(gamePlayStore.selectors.currentQuestion);
	const questionVisible = useGameplaySelector(gamePlayStore.selectors.questionVisible);
	const dispatch = useAppDispatch();

	const answers = FORCE_ANSWER_COUNT
		? (question?.answers ?? []).forceLength(FORCE_ANSWER_COUNT)
		: question?.answers ?? [];

	const textRef = useRef<HTMLSpanElement>(null);
	const lineCount = useLineCount(textRef, question?.text);

	function revealQuestion() {
		if (!questionVisible) {
			dispatch(gamePlayStore.actions.revealQuestion());
		}
	}

	return <Stack spacing={4} sx={styles.container}>
		{question && <Box sx={styles.questionContainer}>
			<FlipCard flipped={questionVisible} style={styles.questionFlip}
				direction='vertical'
				aria-label='Question'
				onClick={revealQuestion}
				front={false}
				back={<Typography ref={textRef} variant="h3"
					sx={[styles.questionText, (lineCount === 1) && styles.questionTextLarge]}>
					{question.text + (devSwitch(false) ? LOREM_IPSUM : '')}
				</Typography>}
			/>
		</Box>}

		<TwoColumns items={answers} sx={styles.answerContainer}
			renderItem={(answer, index) =>
				<AnswerCard key={`answer${index}`} rank={index + 1} answer={answer} />}
		/>
	</Stack>;
};

const styles = muiStyleSheet({
	container: {
		height: '640px',
	},
	questionContainer: (theme) => ({
		textAlign: 'center',
		paddingY: 2,
		paddingX: 4,
		bgcolor: 'rgba(255, 255, 255, 0.9)',
		borderRadius: '16px',
		border: `4px solid ${theme.palette.game.gold}`,
		height: '200px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	}),
	questionFlip: {
		height: '100%',
		width: '100%',
	},
	questionText: (theme) => ({
		fontWeight: 'bold',
		color: theme.palette.game.textDark,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitLineClamp: 3,
		WebkitBoxOrient: 'vertical',
	}),
	questionTextLarge: {
		fontSize: '4rem',
	},
	answerContainer: (theme) => ({
		background: `linear-gradient(180deg, ${theme.palette.game.answerBoard.start} 0%, ${theme.palette.game.answerBoard.end} 100%)`,
		border: `8px solid ${theme.palette.game.white}`,
		borderRadius: '60px',
		paddingX: 6,
		flex: 1,
		alignItems: 'stretch',
		alignContent: 'space-evenly',
	}),
});